import {useQuery} from "@apollo/client";
import {
    PickupAppointmentListingResponse,
    GET_PICKUP_APPOINTMENT_LISTING
} from "../graphql/queries/getPickupAppointmentListing";
import {
    PickupAppointmentCountsResponse,
    GET_PICKUP_APPOINTMENT_COUNTS
} from "../graphql/queries/getPickupAppointmentCounts";
import {useEffect, useState, useMemo, useRef} from "react";
import {PickupAppointmentType, PickupAppointmentFiltersType} from "../graphql/queries/getPickupAppointmentListing";

export interface PickupAppointmentQueryResult {
    appointmentData: PickupAppointmentType[];
    loading: boolean;
    refetch: (variables?: any) => Promise<any>;
    totalElements: number;
    buildQueryVariables: () => any;
    pendingCount: number;
    confirmedCount: number;
    countsLoading: boolean;
    refetchCounts: () => Promise<any>;
}

interface PickupAppointmentQueryOptions {
    fetchAllPages?: boolean;
}

export const usePickupAppointmentQuery = (
    filters: PickupAppointmentFiltersType,
    currentPage: number,
    pageSize: number,
    currentWarehouseId: string | number, // 添加参数：当前真实的 warehouseId
    options: PickupAppointmentQueryOptions = {}
): PickupAppointmentQueryResult => {
    const [appointmentData, setAppointmentData] = useState<PickupAppointmentType[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    const [confirmedCount, setConfirmedCount] = useState(0);
    const [allPagesLoading, setAllPagesLoading] = useState(false);
    const fetchAllPagesRequestIdRef = useRef(0);
    const {fetchAllPages = false} = options;

    // 检查 skip 条件：
    // 1. currentWarehouseId 必须有值（warehouse 已准备好）
    // 2. expectedPickupDate 必须有值
    const shouldSkip: boolean = !currentWarehouseId || !filters.expectedPickupDate;

    // 使用 useMemo 缓存 variables，在构建时自动添加 warehouseId
    const queryVariables = useMemo(() => {
        return {
            filters: {
                ...filters,
                vehicleModelId: undefined,     // 清空旧字段，防止 URL hash 残留被传给后端
                vehicleModelIdList: undefined, // 清空旧字段，防止 URL hash 残留被传给后端
                warehouseId: currentWarehouseId
            },
            pageRequest: {
                page: currentPage,
                size: pageSize,
                sortBy: "pickupDate",
                sortDirection: "DESC"
            }
        };
    }, [filters, currentPage, pageSize, currentWarehouseId]);

    const buildQueryVariables = () => queryVariables;

    // 执行查询
    const {data, loading: queryLoading, refetch, fetchMore} = useQuery<PickupAppointmentListingResponse>(
        GET_PICKUP_APPOINTMENT_LISTING,
        {
            variables: queryVariables,
            // 只有在有 warehouseId 和 expectedPickupDate 时才执行查询
            skip: shouldSkip,
            // 使用 fetchPolicy 避免不必要的重复查询
            fetchPolicy: 'cache-and-network',
            // 设置 notifyOnNetworkStatusChange 为 false，避免状态变化时的重复通知
            notifyOnNetworkStatusChange: false
        }
    );

    // 执行数量查询
    const {data: countsData, loading: countsLoading, refetch: refetchCounts} = useQuery<PickupAppointmentCountsResponse>(
        GET_PICKUP_APPOINTMENT_COUNTS,
        {
            variables: {
                filters: {
                    ...queryVariables.filters
                }
            },
            // 只有在有 warehouseId 和 expectedPickupDate 时才执行查询
            skip: shouldSkip,
            // 使用相同的 fetchPolicy 和 notifyOnNetworkStatusChange 设置
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: false
        }
    );

    // 当数据变化时更新状态
    useEffect(() => {
        const listing = data?.getPickupAppointmentListing;
        const responseData = listing?.content;
        if (!responseData) {
            return;
        }

        const totalElements = listing?.totalElements || 0;
        setTotal(totalElements);

        if (!fetchAllPages) {
            setAppointmentData(responseData);
            setLoading(false);
            return;
        }

        const responsePageSize = listing?.size || responseData.length || pageSize;
        const pageStartIndex = (currentPage - 1) * pageSize;
        const pageEndIndex = Math.min(pageStartIndex + pageSize, totalElements);
        const firstBackendPage = responsePageSize > 0 ? Math.floor(pageStartIndex / responsePageSize) + 1 : currentPage;
        const lastBackendPage = responsePageSize > 0 ? Math.ceil(pageEndIndex / responsePageSize) : currentPage;
        const pagesToFetch = Array.from(
            {length: Math.max(0, lastBackendPage - firstBackendPage + 1)},
            (_, index) => firstBackendPage + index
        );

        if (pagesToFetch.length <= 1 && responseData.length >= pageEndIndex - pageStartIndex) {
            setAppointmentData(responseData);
            setLoading(false);
            setAllPagesLoading(false);
            return;
        }

        const requestId = ++fetchAllPagesRequestIdRef.current;
        setAllPagesLoading(true);

        const fetchRemainingPages = async () => {
            try {
                const pageResults = await Promise.all(
                    pagesToFetch.map(page => {
                        if (page === listing.page) {
                            return Promise.resolve({data});
                        }

                        return fetchMore({
                            variables: {
                                ...queryVariables,
                                pageRequest: {
                                    ...queryVariables.pageRequest,
                                    page,
                                    size: responsePageSize
                                }
                            }
                        });
                    })
                );

                if (fetchAllPagesRequestIdRef.current !== requestId) {
                    return;
                }

                const fetchedData = pageResults.flatMap(result => result.data?.getPickupAppointmentListing?.content || []);
                const firstFetchedIndex = (firstBackendPage - 1) * responsePageSize;
                const sliceStart = Math.max(0, pageStartIndex - firstFetchedIndex);
                const sliceEnd = sliceStart + Math.max(0, pageEndIndex - pageStartIndex);
                setAppointmentData(fetchedData.slice(sliceStart, sliceEnd));
            } catch (error) {
                if (fetchAllPagesRequestIdRef.current === requestId) {
                    console.error('Failed to fetch all pickup appointment pages:', error);
                    setAppointmentData(responseData);
                }
            } finally {
                if (fetchAllPagesRequestIdRef.current === requestId) {
                    setAllPagesLoading(false);
                    setLoading(false);
                }
            }
        };

        fetchRemainingPages();
    }, [currentPage, data, fetchAllPages, fetchMore, pageSize, queryVariables]);

    // 当数量数据变化时更新状态
    useEffect(() => {
        const counts = countsData?.getPickupAppointmentCounts;
        if (counts) {
            setPendingCount(counts.pendingCount);
            setConfirmedCount(counts.confirmedCount);
        }
    }, [countsData]);

    return {
        appointmentData,
        loading: loading || queryLoading || allPagesLoading,
        refetch,
        totalElements: total,
        buildQueryVariables,
        pendingCount,
        confirmedCount,
        countsLoading,
        refetchCounts
    };
};
